import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { formatApiErrorDetail } from "../lib/apiClient";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    if (password.length < 6) { toast.error("Minimum 6 characters"); return; }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login"), 1800);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#0B101E] text-white/70" data-testid="reset-password-page">
        <div className="max-w-md text-center">
          <h1 className="font-display text-3xl mb-3">Invalid reset link</h1>
          <p className="text-white/60 text-sm mb-6">The reset token is missing or malformed.</p>
          <Link to="/forgot-password" className="btn-gold rounded-md px-5 py-3 inline-block">Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#0B101E]" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Set new password</div>
        <h1 className="font-display text-4xl mb-2">Choose a strong password</h1>
        <p className="text-white/60 text-sm mb-8">Make sure it's at least 6 characters.</p>

        {!done ? (
          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50">New password</label>
              <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-[#12182B] border border-white/10 rounded-md px-4 py-3 focus:outline-none focus:border-[#D4AF37]/60"
                data-testid="reset-password-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50">Confirm password</label>
              <input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="mt-2 w-full bg-[#12182B] border border-white/10 rounded-md px-4 py-3 focus:outline-none focus:border-[#D4AF37]/60"
                data-testid="reset-confirm-input" />
            </div>
            <button type="submit" disabled={loading} className="btn-gold w-full rounded-md py-3 disabled:opacity-60" data-testid="reset-submit">
              {loading ? "Updating…" : "Update password"}
            </button>
          </form>
        ) : (
          <div className="bg-[#12182B] border border-[#D4AF37]/30 rounded-xl p-6 text-center" data-testid="reset-done">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 mx-auto flex items-center justify-center text-[#D4AF37]">
              <Check className="w-5 h-5" />
            </div>
            <div className="font-display text-xl mt-4">Password updated</div>
            <p className="text-white/60 text-sm mt-3">Redirecting to sign in…</p>
          </div>
        )}
      </div>
    </div>
  );
}
